// The editor's affordances on the visitor interface (WS-51, contract section 4 "Editor mode"): where each control is
// on screen (`listControls`, the event `controls`), the ghosts of the controls a preset hides, and the outline of what
// is being edited. All of it exists only while the page is in editor mode (bridge.js): a visitor's page never calls
// any of these. The page is handed in (`doc`), so the geometry is tested without a browser.
/** @typedef {import('../bridge.js').ControlRect} ControlRect */
/** @typedef {import('../bridge.js').ScreenRect} ScreenRect */

// A control without an element of its own hangs on another control's element (README: the northern-lights panel is
// the popover of the aurora button): that element is where it is on screen.
const ANCHOR = { 'aurora.panel': '#aurora-btn' };
/** The elements of a control: `data-control="<id>"` for the new ones, the selector the settings list names for main's. */
export const controlSelector = (ctl) => [`[data-control="${ctl.id}"]`, ctl.legacy?.el].filter(Boolean).join(', ');
const isPopover = (el) => el.getAttribute?.('role') === 'dialog'; // an open popover is not where the control sits

/**
 * The union of the boxes of the elements that take room on the page, in CSS pixels from the frame's top left
 * corner; null when none of them does (not on the page, `display: none`, a closed popover).
 * @param {Iterable<Element>} els  @returns {ScreenRect | null}
 */
export function rectOf(els) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const el of els) {
    if (isPopover(el)) continue;
    const r = el.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) continue;
    x0 = Math.min(x0, r.left); y0 = Math.min(y0, r.top); x1 = Math.max(x1, r.right); y1 = Math.max(y1, r.bottom);
  }
  return x1 > x0 ? { x: Math.round(x0), y: Math.round(y0), w: Math.round(x1 - x0), h: Math.round(y1 - y0) } : null;
}

/**
 * Every control of the settings list as the page draws it now. A control the preset hides has a rectangle only as a
 * ghost (its would-be position); a control whose data is missing, or a card part while no card is open, has none.
 * @param {object} o
 * @param {import('../settings/typedefs.js').Registry} o.reg
 * @param {import('../settings/typedefs.js').LayoutDef} o.layout  the layout on screen
 * @param {Set<string>} o.shown   what the preset shows (`runtime.controls()`)
 * @param {Set<string>} o.drawn   the shown controls that are on the page
 * @param {Set<string>} o.ghosts  the hidden controls drawn as ghosts
 * @param {{ querySelectorAll(sel: string): Iterable<Element> }} o.doc
 * @returns {ControlRect[]}
 */
export function controlRects({ reg, layout, shown, drawn, ghosts, doc }) {
  const slots = Object.entries(layout?.slots || {});
  return reg.controls.map((ctl) => {
    const ghost = ghosts.has(ctl.id), on = drawn.has(ctl.id) || ghost;
    const sel = [controlSelector(ctl), ANCHOR[ctl.id]].filter(Boolean).join(', ');
    return {
      id: ctl.id, shown: shown.has(ctl.id), rect: on ? rectOf(doc.querySelectorAll(sel)) : null, ghost,
      parent: ctl.parent ?? null, slot: slots.find(([, ids]) => Array.isArray(ids) && ids.includes(ctl.id))?.[0] || ctl.slot,
    };
  });
}

/**
 * VIS-73: the visitor control a pressed element belongs to (the bridge event `menu`): the innermost element that a
 * control's selector matches wins (a date shortcut, not the dock around it). null = no control.
 * @param {{ controls: Array<{ id: string, legacy?: { el?: string } }> }} reg  @param {Element | null} target  @returns {string | null}
 */
export function controlAt(reg, target) {
  let best = null, at = null;
  for (const ctl of reg.controls) {
    const el = target?.closest?.(controlSelector(ctl));
    if (el && (!at || at.contains(el))) { at = el; best = ctl.id; }
  }
  return best;
}

/** The control a `highlight` target names: the control itself, or the one that lets a visitor change the setting. */
export function controlOf(reg, target) {
  if (typeof target?.control === 'string') return reg.control(target.control);
  const e = typeof target?.setting === 'string' ? reg.entry(target.setting) : null;
  return e ? reg.control(e.control) || reg.controls.find((c) => c.settings.includes(e.key)) || null : null;
}

/**
 * Marks the elements of the ghost controls (dimmed by CSS) and clears the mark from everything else. `whole`: region
 * elements whose every control is a ghost. VIS-73: a ghost can be pressed and works for the editor as the shown
 * control would (the runtime lets its press through: `rt.ghosts`, main.js); visitors never see it.
 */
export function markGhosts(reg, ghosts, doc, whole = []) {
  for (const el of doc.querySelectorAll('[data-ghost]')) el.removeAttribute('data-ghost');
  const mark = (el) => { if (!isPopover(el)) el.setAttribute('data-ghost', ''); };
  for (const id of ghosts) { const ctl = reg.control(id); if (ctl) for (const el of doc.querySelectorAll(controlSelector(ctl))) mark(el); }
  for (const el of whole) mark(el);
}

/** The outline of what is being edited: one box over the page that never takes a press. null removes it. */
export function outline(doc, rect) {
  let el = doc.getElementById('mms-outline');
  if (!rect) { el?.remove(); return; }
  if (!el) { el = doc.createElement('div'); el.id = 'mms-outline'; el.setAttribute('aria-hidden', 'true'); doc.body.appendChild(el); }
  Object.assign(el.style, { left: `${rect.x}px`, top: `${rect.y}px`, width: `${rect.w}px`, height: `${rect.h}px` });
}
